import { useFeature } from '@context'
import { IInvoice, IPostDueScheduleItem, PostDueScheduleState } from '@query'
import { formatDate } from '@utils'
import dayjs from 'dayjs'
import {
  Timeline,
  TimelineBody,
  TimelineNode,
  TimelineRail,
  TimelineRow,
  TimelineStem
} from '@root/components/TimelineRail/TimelineRail.styled'
import { visiblePostDueSchedule } from '@pages/InvoicingPage/postDueUi.ts'
import {
  BankMonitoringVariant,
  useBankMonitoringStatus
} from '@pages/InvoicingPage/useBankMonitoringStatus'
import { BankMonitoringHint } from './BankMonitoringHint'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { IconType } from 'react-icons'
import { FaEnvelope, FaExclamationTriangle, FaFileAlt, FaGavel } from 'react-icons/fa'
import { StartDebtCollectionMessage } from './StartDebtCollectionMessage'
import styled from 'styled-components'

interface Props {
  invoice: IInvoice
  manualCollectionAvailable: boolean
  automaticCollectionEnabled: boolean
}

const REMINDER_PRESENTATION: Record<
  'sent' | 'failed' | 'scheduled' | 'delayed',
  { tone: 'info' | 'failed' | 'scheduled'; titleKey: string; Icon: IconType }
> = {
  sent: {
    tone: 'info',
    titleKey: 'invoicing.debtCollection.reminders.sentByYou',
    Icon: FaEnvelope
  },
  failed: {
    tone: 'failed',
    titleKey: 'invoicing.debtCollection.reminders.failedStep',
    Icon: FaExclamationTriangle
  },
  scheduled: {
    tone: 'scheduled',
    titleKey: 'invoicing.debtCollection.reminders.scheduledStep',
    Icon: FaEnvelope
  },
  delayed: {
    tone: 'scheduled',
    titleKey: 'invoicing.debtCollection.reminders.delayedStep',
    Icon: FaEnvelope
  }
}

const reminderPresentation = (state: PostDueScheduleState, at: string) => {
  if (state === 'failed') return REMINDER_PRESENTATION.failed
  if (state === 'sent') return REMINDER_PRESENTATION.sent
  if (dayjs(at).isBefore(dayjs().startOf('day'))) return REMINDER_PRESENTATION.delayed
  return REMINDER_PRESENTATION.scheduled
}

const isDelayedReminder = (item: IPostDueScheduleItem) =>
  item.kind === 'reminder' &&
  reminderPresentation(item.state, item.at) === REMINDER_PRESENTATION.delayed

export const SoftReminderTracker: React.FC<Props> = ({
  invoice,
  manualCollectionAvailable,
  automaticCollectionEnabled
}) => {
  const { t } = useTranslation()
  const { enabled: kraviaFeature } = useFeature('kravia')
  const { variant: bankMonitoringVariant } = useBankMonitoringStatus()
  const items = visiblePostDueSchedule(invoice, {
    kraviaFeature,
    manualCollectionAvailable,
    automaticCollectionEnabled
  })
  const invoicingEmail = invoice.receiver_info?.invoicing_email
  const hasCollection = items.some(item => item.kind === 'collection')

  if (items.length === 0) return null

  const reminderMeta = (at: string): string => {
    if (invoicingEmail) {
      return t('invoicing.debtCollection.reminders.eventMetaTo', {
        date: formatDate(at),
        email: invoicingEmail
      })
    }
    return t('invoicing.debtCollection.reminders.eventMeta', {
      date: formatDate(at)
    })
  }

  const lastIndex = items.length - 1
  const firstDelayedIndex = items.findIndex(isDelayedReminder)

  return (
    <Schedule>
      <Eyebrow>
        {t(
          hasCollection
            ? 'invoicing.debtCollection.reminders.collectionHistoryEyebrow'
            : 'invoicing.debtCollection.reminders.historyEyebrow'
        )}
      </Eyebrow>
      <Timeline>
        {items.map((item, index) => (
          <ScheduleRow
            key={`${item.kind}-${item.at}-${index}`}
            item={item}
            invoice={invoice}
            isLast={index === lastIndex}
            reminderMeta={reminderMeta}
            bankMonitoringVariant={
              bankMonitoringVariant && index === firstDelayedIndex ? bankMonitoringVariant : null
            }
            deliveryMethodLabel={
              item.delivery_method
                ? t(`invoicing.deliveryMethod.${item.delivery_method}`)
                : t(`invoicing.deliveryMethod.${invoice.delivery_method}`)
            }
          />
        ))}
      </Timeline>
    </Schedule>
  )
}

const ScheduleRow: React.FC<{
  item: IPostDueScheduleItem
  invoice: IInvoice
  isLast: boolean
  reminderMeta: (at: string) => string
  deliveryMethodLabel: string
  bankMonitoringVariant: BankMonitoringVariant | null
}> = ({ item, invoice, isLast, reminderMeta, deliveryMethodLabel, bankMonitoringVariant }) => {
  const { t } = useTranslation()

  const body = (() => {
    switch (item.kind) {
      case 'invoice_sent':
        return {
          tone: 'neutral' as const,
          icon: <FaFileAlt />,
          title: t('invoicing.debtCollection.reminders.invoiceSent'),
          meta: t('invoicing.debtCollection.reminders.invoiceSentMeta', {
            date: formatDate(item.at),
            method: deliveryMethodLabel,
            dueDate: formatDate(invoice.due_date)
          }),
          extra: null
        }
      case 'reminder': {
        const presentation = reminderPresentation(item.state, item.at)
        const Icon = presentation.Icon
        const step = Math.min(Math.max(item.reminder_number ?? 1, 1), 5)
        return {
          tone: presentation.tone,
          icon: bankMonitoringVariant ? <WarningNodeIcon /> : <Icon />,
          title: t(presentation.titleKey, {
            stepOrdinal: t(`invoicing.debtCollection.reminders.ordinal.step${step}`),
            stepOrdinalFailed: t(`invoicing.debtCollection.reminders.ordinalFailed.step${step}`)
          }),
          meta: reminderMeta(item.at),
          extra: null
        }
      }
      case 'collection': {
        switch (item.state) {
          case 'scheduled':
            return {
              tone: 'scheduled' as const,
              icon: <FaGavel />,
              title: t('invoicing.debtCollection.reminders.collectionScheduled'),
              meta: t('invoicing.debtCollection.reminders.eventMeta', {
                date: formatDate(item.at)
              }),
              extra: null
            }
          case 'automatic':
            return {
              tone: 'collection' as const,
              icon: <FaGavel />,
              title: t('invoicing.debtCollection.reminders.collectionAutomatic'),
              meta: t('invoicing.debtCollection.reminders.collectionAutomaticHint', {
                date: formatDate(item.at)
              }),
              extra: null
            }
          case 'waiting':
            return {
              tone: 'collection' as const,
              icon: <FaGavel />,
              title: null,
              meta: null,
              extra: (
                <StartDebtCollectionMessage
                  invoice={invoice}
                  phase="waiting"
                  handoverAt={item.at}
                />
              )
            }
          case 'ready':
            return {
              tone: 'collection' as const,
              icon: <FaGavel />,
              title: null,
              meta: null,
              extra: (
                <StartDebtCollectionMessage invoice={invoice} phase="ready" handoverAt={item.at} />
              )
            }
          case 'sent':
            return {
              tone: 'collection' as const,
              icon: <FaGavel />,
              title: t('invoicing.debtCollection.reminders.collectionSent'),
              meta: t('invoicing.debtCollection.reminders.eventMeta', {
                date: formatDate(item.at)
              }),
              extra: null
            }
          case 'failed':
            return {
              tone: 'failed' as const,
              icon: <FaExclamationTriangle />,
              title: t('invoicing.debtCollection.reminders.collectionFailed'),
              meta: t('invoicing.debtCollection.reminders.eventMeta', {
                date: formatDate(item.at)
              }),
              extra: null
            }
          default: {
            const exhaustive: never = item.state
            return exhaustive
          }
        }
      }
      default: {
        const exhaustive: never = item.kind
        return exhaustive
      }
    }
  })()

  const row = (
    <TimelineRow>
      <TimelineRail $compact>
        <TimelineNode $compact $tone={body.tone}>
          {body.icon}
        </TimelineNode>
        {!isLast && <TimelineStem />}
      </TimelineRail>
      <TimelineBody $pad={isLast ? 'none' : 'sm'}>
        {body.extra ? (
          body.extra
        ) : (
          <>
            <Title>{body.title}</Title>
            <Meta>{body.meta}</Meta>
          </>
        )}
      </TimelineBody>
    </TimelineRow>
  )

  if (!bankMonitoringVariant) return row

  return <BankMonitoringHint variant={bankMonitoringVariant}>{row}</BankMonitoringHint>
}

const Schedule = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.dashboard.textSubtle};
`

const Title = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 700;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.menuBg};
`

const WarningNodeIcon = styled(FaExclamationTriangle)`
  color: ${({ theme }) => theme.colors.warning};
`

const Meta = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.iconGray};
`
